import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, Images, ArrowRight, FolderOpen } from "lucide-react";
import { listProjects, projectImageUrl } from "@/lib/api";
import { PROJECT_CATEGORIES, findCategory } from "@/lib/projectCategories";

export const ProjectFolder = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCategory, setOpenCategory] = useState(null); // slug or null
  const [preview, setPreview] = useState(null); // lightbox image id

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await listProjects();
        if (alive) setImages(data);
      } catch {
        if (alive) setImages([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Listen for cross-component requests to open a specific folder
  useEffect(() => {
    const handler = (e) => {
      const slug = e.detail?.slug;
      if (!slug) return;
      const target = findCategory(slug);
      if (!target) return;
      document.getElementById("projekt")?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => setOpenCategory(target.slug), 450);
    };
    window.addEventListener("swedcro:open-folder", handler);
    return () => window.removeEventListener("swedcro:open-folder", handler);
  }, []);

  // Group images by category
  const grouped = useMemo(() => {
    const map = {};
    for (const cat of PROJECT_CATEGORIES) map[cat.slug] = [];
    for (const img of images) {
      const slug = img.category && map[img.category] ? img.category : PROJECT_CATEGORIES[0].slug;
      map[slug].push(img);
    }
    return map;
  }, [images]);

  const currentCategory = openCategory ? findCategory(openCategory) : null;
  const currentImages = openCategory ? grouped[openCategory] || [] : [];

  return (
    <section
      id="projekt"
      data-testid="project-folder-section"
      className="py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        {/* Section header */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#FFB3B3]">
            <span className="w-8 h-[2px] bg-[#FFB3B3]" /> 03 — Projekt
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white mt-3 leading-[1]">
            Våra
            <br />
            <span className="text-[#FFB3B3]">senaste projekt.</span>
          </h2>
          <p className="font-body text-white/80 mt-4 max-w-xl">
            Utforska våra arbeten kategori för kategori. Klicka på en mapp för att öppna galleriet.
          </p>
        </div>

        {/* 4 category folders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PROJECT_CATEGORIES.map((cat, idx) => {
            const catImages = grouped[cat.slug] || [];
            const count = catImages.length;
            const cover = catImages[0];
            const thumbs = catImages.slice(1, 4);

            return (
              <button
                key={cat.slug}
                type="button"
                data-testid={`project-folder-${cat.slug}`}
                onClick={() => setOpenCategory(cat.slug)}
                className="group text-left brut-card overflow-hidden hover:shadow-[10px_10px_0_#D92525] hover:-translate-y-1 transition-all bg-white flex flex-col"
              >
                {/* Cover area */}
                <div className="relative h-56 md:h-64 overflow-hidden bg-[#0B132B]">
                  {cover ? (
                    <img
                      src={projectImageUrl(cover.id)}
                      alt={cover.filename}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0B132B] via-[#3a1550] to-[#6b0e18]">
                      <FolderOpen size={64} strokeWidth={1.4} className="text-white/40" />
                    </div>
                  )}

                  {/* Category number badge */}
                  <div className="absolute top-4 left-4 bg-white text-[#0B132B] font-display font-black text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5">
                    Mapp 0{idx + 1}
                  </div>

                  {/* Count pill */}
                  <div className="absolute top-4 right-4 bg-[#D92525] text-white font-display font-black text-xs tracking-[0.15em] uppercase px-3 py-1.5 flex items-center gap-1.5">
                    <Images size={14} />
                    {count} {count === 1 ? "bild" : "bilder"}
                  </div>

                  {/* Bottom gradient + small thumbs */}
                  {thumbs.length > 0 && (
                    <div className="absolute inset-x-0 bottom-0 p-3 flex gap-1.5 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                      {thumbs.map((t) => (
                        <div
                          key={t.id}
                          className="w-14 h-14 border-2 border-white/80 shrink-0 overflow-hidden"
                        >
                          <img
                            src={projectImageUrl(t.id)}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Text content */}
                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display font-black text-2xl md:text-3xl text-[#0B132B] tracking-tight leading-tight">
                      {cat.title}
                    </h3>
                    <ArrowRight
                      size={22}
                      strokeWidth={2.4}
                      className="text-[#0B132B]/40 group-hover:text-[#D92525] group-hover:translate-x-1 transition-all shrink-0 mt-1"
                    />
                  </div>
                  <p className="font-body text-[#4B5563] text-sm leading-relaxed mt-3 flex-1">
                    {cat.description}
                  </p>
                  <div className="mt-5 pt-4 border-t-2 border-[#0B132B] font-display font-bold text-xs tracking-[0.15em] uppercase text-[#D92525] inline-flex items-center gap-1.5">
                    Öppna mapp <span aria-hidden>→</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="mt-8 font-body text-white/60 text-sm">Laddar bilder…</div>
        )}
      </div>

      {/* Category Dialog */}
      <Dialog
        open={!!openCategory}
        onOpenChange={(v) => {
          if (!v) {
            setOpenCategory(null);
            setPreview(null);
          }
        }}
      >
        <DialogContent
          data-testid="project-folder-dialog"
          className="flex flex-col gap-0 w-[calc(100vw-1.5rem)] max-w-6xl max-h-[90vh] overflow-hidden bg-[#0B132B] border-2 border-white/20 p-0 [&>button:last-child]:hidden"
        >
          <div className="flex items-start justify-between gap-3 border-b-2 border-white/15 px-4 py-3 md:px-6 md:py-4 shrink-0">
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-display font-black text-xl md:text-3xl text-white tracking-tight break-words leading-tight">
                {currentCategory?.title}
              </DialogTitle>
              <DialogDescription className="font-body text-xs text-white/60 mt-1 line-clamp-2">
                {currentCategory?.description}
              </DialogDescription>
            </div>
            <button
              data-testid="project-folder-dialog-close"
              onClick={() => {
                setOpenCategory(null);
                setPreview(null);
              }}
              className="p-2 border-2 border-white/50 text-white hover:bg-white hover:text-[#0B132B] transition-colors shrink-0"
              aria-label="Stäng"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain p-3 md:p-5">
            {currentImages.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-white/20 bg-white/[0.03]">
                <FolderOpen size={40} className="text-white/40 mx-auto" strokeWidth={1.6} />
                <div className="font-display font-bold text-white text-lg mt-3">Inga bilder ännu</div>
                <div className="font-body text-white/60 text-sm mt-1">Bilder i denna kategori dyker upp här.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {currentImages.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setPreview(img)}
                    data-testid={`project-image-${img.id}`}
                    className="relative w-full aspect-[4/3] sm:aspect-square overflow-hidden border-2 border-white/15 hover:border-[#D92525] transition-colors group"
                  >
                    <img
                      src={projectImageUrl(img.id)}
                      alt={img.filename}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent
          data-testid="project-lightbox"
          className="w-[calc(100vw-1.5rem)] max-w-5xl max-h-[92vh] p-0 bg-black border-2 border-white/20 overflow-hidden flex items-center justify-center [&>button:last-child]:hidden"
        >
          <DialogTitle className="sr-only">{preview?.filename}</DialogTitle>
          <DialogDescription className="sr-only">Förstorad projektbild</DialogDescription>
          <button
            onClick={() => setPreview(null)}
            data-testid="project-lightbox-close"
            className="absolute top-3 right-3 z-10 p-2.5 bg-black/70 border-2 border-white/60 text-white hover:bg-white hover:text-black transition-colors"
            aria-label="Stäng"
          >
            <X size={16} />
          </button>
          {preview && (
            <img
              src={projectImageUrl(preview.id)}
              alt={preview.filename}
              className="block max-w-full w-auto h-auto max-h-[calc(92vh-4px)] object-contain mx-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
